import { useState } from "react";
import { toast } from "sonner";
import { C, PageHeader, GlassCard, Button, Badge } from "@coremonit/design-system";
import { DownloadCloud, FileText, FileJson, FileSpreadsheet, FileCode, Grid3x3, LineChart, PackagePlus } from "lucide-react";
import { getToken } from "../api/client";

const ENTITIES = [
  { id: "services",    label: "Services" },
  { id: "deployments", label: "Deployments" },
  { id: "logs",        label: "Recent logs" },
  { id: "traces",      label: "Traces" },
  { id: "metrics",     label: "Metrics snapshot" },
];

const FORMATS = [
  { id: "json",       label: "JSON",        icon: <FileJson size={14} />,        color: C.blue },
  { id: "csv",        label: "CSV",         icon: <FileText size={14} />,        color: C.cyan },
  { id: "excel",      label: "Excel",       icon: <FileSpreadsheet size={14} />, color: C.green },
  { id: "pdf",        label: "PDF",         icon: <FileText size={14} />,        color: C.red },
  { id: "prometheus", label: "Prometheus",  icon: <LineChart size={14} />,       color: C.amber },
  { id: "grafana",    label: "Grafana",     icon: <Grid3x3 size={14} />,         color: C.purple },
  { id: "otel",       label: "OpenTelemetry", icon: <PackagePlus size={14} />,   color: C.pink },
];

export function ExportPage() {
  const [entity, setEntity] = useState("services");
  const [busy, setBusy] = useState(false);

  const download = async (format: string) => {
    setBusy(true);
    try {
      const url = new URL("/export", import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000");
      url.searchParams.set("format", format);
      url.searchParams.set("entity", entity);
      const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${getToken() ?? ""}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `${entity}.${format === "prometheus" ? "prom" : format}`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success(`Exported ${entity} as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<DownloadCloud size={16} />} title="Export" subtitle="PDF · CSV · Excel · JSON · Prometheus · Grafana · OpenTelemetry" />

      <GlassCard style={{ padding: 14 }}>
        <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>1 · pick data</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ENTITIES.map((e) => (
            <button key={e.id} onClick={() => setEntity(e.id)}
              style={{
                padding: "6px 12px", borderRadius: 8,
                background: entity === e.id ? C.blueDim : C.surfaceHigh,
                border: `1px solid ${entity === e.id ? C.borderBright : C.border}`,
                color: entity === e.id ? C.blue : C.textSub,
                fontSize: 11, fontWeight: 700, cursor: "pointer",
              }}
            >{e.label}</button>
          ))}
        </div>
      </GlassCard>

      <GlassCard style={{ padding: 14 }}>
        <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>2 · pick format</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
          {FORMATS.map((f) => (
            <button key={f.id} onClick={() => download(f.id)} disabled={busy}
              style={{
                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                padding: 12, background: C.surfaceHigh,
                border: `1px solid ${C.border}`, borderRadius: 12,
                color: C.text, cursor: "pointer",
                transition: "border-color 0.15s, background 0.15s",
                opacity: busy ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { if (!busy) e.currentTarget.style.borderColor = f.color; }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
            >
              <span style={{ color: f.color }}>{f.icon}</span>
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{f.label}</div>
                <div style={{ color: C.textMuted, fontSize: 10 }}>{entity}.{f.id === "prometheus" ? "prom" : f.id}</div>
              </div>
            </button>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}
